import * as React from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useAuth } from "@/hooks/useAuth";
import { usePortfolioConsultation } from "@/hooks/useCheckout";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";

/**
 * Public marketing home page. Rebuilt from the HosterDash landing HTML using
 * the app's Tailwind/brand tokens so it shares one design system with the rest
 * of the product. Anyone can view it; the consultation form posts to the same
 * lead endpoint the Portfolio dialog uses.
 */
export function HomePage() {
  const { status } = useAuth();
  const authed = status === "authenticated";

  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <HomeNav authed={authed} />
      <Hero />
      <Services />
      <PricingCta />
      <HowItWorks />
      <About />
      <Compatibility />
      <Faq />
      <Consultation />
      <FinalCta />
      <Footer />
    </div>
  );
}

// ── Nav ──────────────────────────────────────────────────────
function HomeNav({ authed }: { authed: boolean }) {
  const [open, setOpen] = React.useState(false);
  const links = [
    { href: "#services", label: "Services" },
    { href: "#pricing", label: "Pricing" },
    { href: "#how-it-works", label: "How It Works" },
    { href: "#about", label: "About" },
    { href: "#faq", label: "FAQ" },
    { href: "#consultation", label: "Contact" }
  ];
  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex max-w-[1180px] items-center justify-between px-4 py-3.5 sm:px-6">
        <Link to="/" className="flex items-center">
          <BrandLogo className="h-8 w-auto" />
        </Link>

        <ul className="hidden items-center gap-7 text-sm font-medium text-muted-foreground lg:flex">
          {links.map(l => (
            <li key={l.href}>
              <a href={l.href} className="transition hover:text-foreground">{l.label}</a>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-3 lg:flex">
          {authed ? (
            <Button asChild><Link to="/dashboard">Go to dashboard</Link></Button>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-muted-foreground transition hover:text-foreground">
                Client Login
              </Link>
              <Button asChild><Link to="/plans">Get Started</Link></Button>
            </>
          )}
        </div>

        <button className="lg:hidden" onClick={() => setOpen(v => !v)} aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-background px-4 py-3 lg:hidden">
          <ul className="flex flex-col gap-1 text-sm font-medium">
            {links.map(l => (
              <li key={l.href}>
                <a href={l.href} onClick={() => setOpen(false)} className="block rounded-md px-2 py-2 hover:bg-muted">{l.label}</a>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-col gap-2">
            {authed ? (
              <Button asChild><Link to="/dashboard">Go to dashboard</Link></Button>
            ) : (
              <>
                <Button variant="outline" asChild><Link to="/login">Client Login</Link></Button>
                <Button asChild><Link to="/plans">Get Started</Link></Button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ─────────────────────────────────────────────────────
function Hero() {
  const trust = ["Free SSL Included", "Gmail & Outlook Compatible", "Promonu CRM Ready", "DNS Fully Managed"];
  return (
    <section className="brand-gradient-bg relative overflow-hidden">
      <div className="mx-auto grid max-w-[1180px] items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
        <div>
          {/* <span className="brand-pill-recommended inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
            Business Infrastructure Platform
          </span> */}
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl">
            Business email &amp; hosting<br />
            without the <span className="text-brand-accent">complexity.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            HosterDash gives business owners professional email, reliable hosting, and DNS
            management — all set up for you, all in one place. No technical knowledge required.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button size="lg" asChild><Link to="/plans">View Plans &amp; Pricing →</Link></Button>
            <Button size="lg" variant="outline" asChild><a href="#how-it-works">See How It Works</a></Button>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-2">
            {trust.map(t => (
              <div key={t} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4 shrink-0 text-brand-green" /> {t}
              </div>
            ))}
          </div>
        </div>

        <DashboardMockup />
      </div>
    </section>
  );
}

function DashboardMockup() {
  return (
    <div className="rounded-2xl border border-border bg-background shadow-brand-lg">
      <div className="flex items-center justify-between rounded-t-2xl border-b border-border bg-muted/40 px-4 py-2.5">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
        </div>
        <span className="text-xs font-medium text-muted-foreground">HosterDash Dashboard</span>
        <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[10px] font-semibold text-brand-green">Live</span>
      </div>
      <div className="space-y-4 p-5">
        <div>
          <p className="font-display text-lg font-bold">Welcome back! 👋</p>
          <p className="text-xs text-muted-foreground">yourdomain.com · Everything is running smoothly</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[["Mailboxes", "10"], ["Domains", "3"], ["Uptime", "99.9%"]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-border bg-muted/30 p-3 text-center">
              <p className="text-[11px] text-muted-foreground">{l}</p>
              <p className="font-display text-xl font-bold">{v}</p>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {[
            ["📧", "Business Email", "yourdomain.com"],
            ["🌐", "Website Hosting", "yourdomain.com"],
            ["🔒", "DNS Management", "SPF · DKIM · DMARC"]
          ].map(([icon, name, sub]) => (
            <div key={name} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-accentSoft text-sm">{icon}</span>
                <div>
                  <p className="text-sm font-semibold">{name}</p>
                  <p className="text-[11px] text-muted-foreground">{sub}</p>
                </div>
              </div>
              <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[10px] font-semibold text-brand-green">Active</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Services ─────────────────────────────────────────────────
function Services() {
  const cards = [
    {
      badge: "Starting at $19/mo", icon: "📧", title: "Business Email Hosting", type: "email",
      desc: "Professional email on your own domain. Works with Gmail, Outlook, and every device. We handle the technical setup for you.",
      features: ["Custom email addresses", "Gmail & Outlook compatible", "SPF, DKIM, DMARC setup", "Webmail included", "Promonu CRM compatible"]
    },
    {
      badge: "Starting at $29/mo", icon: "🌐", title: "Website Hosting", type: "hosting",
      desc: "Fast, secure, and reliable hosting for your business websites. Includes email, SSL, and DNS management all in one.",
      features: ["SSD Storage & LiteSpeed", "Free SSL Certificate", "Daily Backups", "1-Click WordPress Install", "Email & DNS Included"]
    },
    {
      badge: "Starting at $99/mo", icon: "🏢", title: "Portfolio Enterprise", type: "portfolio",
      desc: "Manage multiple domains, websites, and clients from one powerful platform. Built for agencies and multi-brand businesses.",
      features: ["Multi-domain Management", "Centralized Dashboard", "Priority Support", "White-label Ready", "Advanced Security"]
    }
  ];
  return (
    <Section id="services" eyebrow="Our Services" title="Everything your business needs online." sub="Three core services. One platform. Zero headaches.">
      <div className="grid gap-6 md:grid-cols-3">
        {cards.map(c => (
          <div key={c.title} className="relative flex flex-col rounded-2xl border border-border bg-background p-7 shadow-brand-sm transition hover:-translate-y-0.5 hover:shadow-brand-lg">
            <span className="absolute right-5 top-5 rounded-full bg-brand-accentSoft px-2.5 py-1 text-[11px] font-semibold text-brand-accentDark">{c.badge}</span>
            <span className="text-3xl">{c.icon}</span>
            <h3 className="mt-4 font-display text-xl font-bold">{c.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
            <ul className="mt-5 flex-1 space-y-2.5">
              {c.features.map(f => (
                <li key={f} className="flex items-start gap-2 text-sm text-foreground/90">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" /> {f}
                </li>
              ))}
            </ul>
            <Link to={`/plans?type=${c.type}`} className="mt-6 text-sm font-semibold text-brand-accent hover:underline">
              View Plans &amp; Pricing →
            </Link>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ── Pricing CTA band ─────────────────────────────────────────
function PricingCta() {
  return (
    <section id="pricing" className="px-4 pb-20 sm:px-6">
      <div className="mx-auto max-w-[1180px]">
        <div className="rounded-3xl border border-brand-accentLighter bg-brand-accentSoft px-8 py-14 text-center">
          <span className="brand-pill-recommended">Simple, transparent pricing</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight">Plans starting from $19/month</h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            Choose from Email Hosting, Website Hosting, or Portfolio Enterprise. Monthly, quarterly,
            and annual billing available. No hidden fees.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button variant="outline" asChild><Link to="/plans?type=email">📧 Email Plans</Link></Button>
            <Button variant="outline" asChild><Link to="/plans?type=hosting">🌐 Hosting Plans</Link></Button>
            <Button asChild><Link to="/plans?type=portfolio">🏢 Portfolio Plans</Link></Button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── How it works ─────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    ["1", "Choose Your Plan", "Pick the perfect service for your business needs. Email only, full hosting, or portfolio enterprise — we have a plan for every stage."],
    ["2", "We Handle the Setup", "We configure everything for you — email accounts, DNS records, SSL, and security. Our team tests everything before handing it over."],
    ["3", "You're Ready to Go", "Start using your professional email and hosting right away. We send you a step-by-step setup guide for your devices."]
  ];
  return (
    <div className="bg-muted/40">
      <Section id="how-it-works" center eyebrow="How It Works" title="Get your business online in 3 simple steps" sub="No technical knowledge needed. We handle the hard stuff.">
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map(([n, t, d]) => (
            <div key={n} className="rounded-2xl border border-border bg-background p-7 text-center shadow-brand-sm">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-brand-accent font-display text-lg font-bold text-white">{n}</div>
              <h3 className="mt-4 font-display text-lg font-bold">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ── About ────────────────────────────────────────────────────
function About() {
  const stats = [
    ["300+", "Domains Managed", "text-brand-accent"],
    ["99.9%", "Uptime SLA", "text-brand-green"],
    ["24h", "Setup Time", "text-amber-600"],
    ["4", "Client Types Served", "text-violet-600"]
  ];
  const points = [
    "Fully managed — we configure everything for you",
    "Compatible with Promonu / GoHighLevel CRM",
    "Works with Shopify, WordPress, Wix, and more",
    "Human support — not just a knowledge base"
  ];
  return (
    <Section id="about">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <span className="brand-pill-recommended">About HosterDash</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight">Built for business owners, not tech teams.</h2>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            HosterDash is the hosting and infrastructure division of Promonu. We built HosterDash
            because we saw too many business owners struggling with email setup, DNS records, and
            hosting configurations that should just work.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Our platform feels more like a modern SaaS product than a traditional hosting reseller.
            You get professional infrastructure with the simplicity of a consumer app.
          </p>
          <div className="mt-7 space-y-3">
            {points.map(p => (
              <div key={p} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-brand-green" /> {p}</div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {stats.map(([v, l, color]) => (
            <div key={l} className="rounded-2xl border border-border bg-background p-7 text-center shadow-brand-sm">
              <div className={`font-display text-4xl font-extrabold ${color}`}>{v}</div>
              <div className="mt-1.5 text-[13px] text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ── Compatibility ────────────────────────────────────────────
function Compatibility() {
  const items = [
    ["📧", "Gmail", "Send and receive from Gmail using your business address"],
    ["💼", "Outlook", "Full compatibility with Microsoft Outlook desktop and mobile"],
    ["📱", "iPhone & Android", "Native mail app setup on any smartphone or tablet"],
    ["⚡", "Promonu / GHL", "Full DNS and domain compatibility with GoHighLevel CRM"],
    ["🛍️", "Shopify", "Point your Shopify domain DNS to HosterDash email"],
    ["🔵", "WordPress", "Full hosting and email for WordPress websites"],
    ["☁️", "Cloudflare", "Compatible with Cloudflare DNS management"],
    ["🔒", "SSL & Security", "Free SSL certificates and SPF, DKIM, DMARC authentication"]
  ];
  return (
    <div className="bg-muted/40">
      <Section center eyebrow="Compatibility" title="Works with everything you already use." sub="HosterDash integrates seamlessly with the tools your business depends on.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(([icon, t, d]) => (
            <div key={t} className="rounded-2xl border border-border bg-background p-5 text-center shadow-brand-sm">
              <span className="text-2xl">{icon}</span>
              <h4 className="mt-2 font-display text-sm font-bold">{t}</h4>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ── FAQ ──────────────────────────────────────────────────────
function Faq() {
  const faqs = [
    ["How does business email setup work?", "After you purchase a plan, fill out our onboarding form with your domain registrar and requirements. Our team then creates your email accounts, configures your DNS records (MX, SPF, DKIM, DMARC), and sends you a setup guide for connecting to Gmail, Outlook, iPhone, or Android. Most setups are complete within 24 hours."],
    ["Can I use my existing domain?", "Yes. You keep your domain at your current registrar (GoDaddy, Namecheap, Cloudflare, Shopify, etc.). We simply give you the DNS records to add, which point your domain's email to HosterDash. Your website continues to work as normal."],
    ["Is HosterDash compatible with Gmail and Outlook?", "Yes — fully compatible. You can connect your HosterDash business email to Gmail so you send and receive from your Gmail inbox using your business address. Same for Outlook desktop, Outlook mobile, iPhone Mail, and Android Gmail or Mail apps."],
    ["Does it work with Promonu / GoHighLevel?", "Yes. HosterDash is built by the same team behind Promonu. If your website is hosted on GoHighLevel, our team handles the special DNS configuration required for GHL domain connections alongside your HosterDash email hosting."],
    ["Can I use HosterDash with my Shopify store?", "Absolutely. Many of our customers run Shopify stores and use HosterDash for their business email. We configure the DNS so your Shopify store continues to work while your email is professionally hosted on HosterDash."],
    ["What is Portfolio Enterprise for?", "Portfolio Enterprise is designed for agencies, startup incubators, and multi-brand business owners who manage multiple domains and websites. Instead of separate accounts for each brand, everything is managed from one centralized dashboard. Plans start at $99/month for up to 5 domains."],
    ["How does DNS management work?", "We handle DNS for you. After purchase, you tell us where your domain is registered and we give you the exact records to add. We also help troubleshoot any DNS issues and support mixed environments where your website is at Shopify or GHL and email is at HosterDash."]
  ];
  const [open, setOpen] = React.useState<number | null>(0);
  return (
    <Section id="faq" center eyebrow="FAQ" title="Common questions answered." sub="Everything you need to know about HosterDash.">
      <div className="mx-auto max-w-3xl space-y-3">
        {faqs.map(([q, a], i) => (
          <div key={q} className="rounded-xl border border-border bg-background">
            <button onClick={() => setOpen(open === i ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold">
              {q}
              <span className="text-lg text-brand-accent">{open === i ? "−" : "+"}</span>
            </button>
            {open === i && <p className="px-5 pb-4 text-sm leading-relaxed text-muted-foreground">{a}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
}

// ── Consultation form ────────────────────────────────────────
function Consultation() {
  const { toast } = useToast();
  const consult = usePortfolioConsultation();
  const [submitted, setSubmitted] = React.useState(false);
  const [form, setForm] = React.useState({
    fullName: "", email: "", service: "Email Only", promonu: "No",
    registrar: "", mailboxes: 1, domain: ""
  });
  const set = (k: string, v: string | number) => setForm(f => ({ ...f, [k]: v }));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (consult.isPending) return;
    if (!form.fullName.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || !form.registrar || !form.domain.trim()) {
      toast({ title: "Please complete the form", description: "Name, a valid email, registrar and domain are required.", variant: "destructive" });
      return;
    }
    const notes = [
      `Service: ${form.service}`,
      `On Promonu/GHL: ${form.promonu}`,
      `Registrar: ${form.registrar}`,
      `Mailboxes: ${form.mailboxes}`
    ].join(" · ");
    try {
      await consult.mutateAsync({
        full_name: form.fullName.trim(),
        email: form.email.trim(),
        domain: form.domain.trim(),
        notes
      });
      setSubmitted(true);
    } catch (err) {
      toast({ title: "Something went wrong", description: err instanceof ApiError ? err.message : "Please try again.", variant: "destructive" });
    }
  }

  const points = [
    "Free 15-minute consultation",
    "Custom DNS advice for your stack",
    "Promonu / GHL workflow guidance",
    "Migration support available"
  ];

  return (
    <Section id="consultation">
      <div className="grid items-start gap-12 lg:grid-cols-2">
        <div>
          <span className="brand-pill-recommended">Get in Touch</span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight">Not sure which plan is right for you?</h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Tell us about your business and we'll recommend the perfect setup. No sales pressure —
            just honest advice from our team.
          </p>
          <div className="mt-6 space-y-3">
            {points.map(p => <div key={p} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-brand-green" /> {p}</div>)}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-background p-7 shadow-brand-sm">
          {submitted ? (
            <div className="py-10 text-center">
              <div className="text-5xl">✅</div>
              <h3 className="mt-4 font-display text-xl font-bold">Request Received!</h3>
              <p className="mt-2 text-sm text-muted-foreground">Our team will be in touch within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <h3 className="font-display text-lg font-bold">Start Your Onboarding</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Full Name"><input className={inputCls} value={form.fullName} onChange={e => set("fullName", e.target.value)} placeholder="e.g. Ahmad Khan" /></Field>
                <Field label="Email Address"><input type="email" className={inputCls} value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@yourbusiness.com" /></Field>
              </div>
              <Field label="What do you need?">
                <select className={inputCls} value={form.service} onChange={e => set("service", e.target.value)}>
                  <option>Email Only</option><option>Website Hosting</option><option>Portfolio Enterprise</option>
                </select>
              </Field>
              <Field label="Will your website be hosted on Promonu / GHL?">
                <select className={inputCls} value={form.promonu} onChange={e => set("promonu", e.target.value)}>
                  <option>No</option><option>Yes</option><option>Not sure</option>
                </select>
              </Field>
              <Field label="Where is your domain registered?">
                <select className={inputCls} value={form.registrar} onChange={e => set("registrar", e.target.value)}>
                  <option value="">Select your registrar</option>
                  <option>GoDaddy</option><option>Namecheap</option><option>Shopify</option><option>Cloudflare</option><option>Other</option>
                </select>
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Mailboxes needed">
                  <input type="number" min={1} max={100} className={inputCls} value={form.mailboxes}
                    onChange={e => set("mailboxes", Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))} />
                </Field>
                <Field label="Your domain name"><input className={inputCls} value={form.domain} onChange={e => set("domain", e.target.value)} placeholder="e.g. yourbusiness.com" /></Field>
              </div>
              <Button type="submit" size="lg" className="w-full" disabled={consult.isPending}>
                {consult.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit & Start Onboarding →"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">Our team will respond within 24 hours.</p>
            </form>
          )}
        </div>
      </div>
    </Section>
  );
}

const inputCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

// ── Final CTA + Footer ───────────────────────────────────────
function FinalCta() {
  return (
    <section className="px-4 pb-20 sm:px-6">
      <div className="mx-auto max-w-[1180px] rounded-3xl border border-border bg-foreground px-8 py-14 text-center text-background">
        <span className="brand-pill-recommended">Ready to get started?</span>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-background">Professional email &amp; hosting — set up for you.</h2>
        <p className="mx-auto mt-3 max-w-2xl text-background/70">
          Join businesses using HosterDash for their infrastructure. Plans from $19/month. Setup in 24 hours.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Button size="lg" variant="secondary" asChild><Link to="/plans">View All Plans →</Link></Button>
          <Button size="lg" variant="outline" className="border-background/30 bg-transparent text-background hover:bg-background/10" asChild>
            <a href="#consultation">Talk to Our Team</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto grid max-w-[1180px] gap-8 px-4 py-12 sm:px-6 md:grid-cols-4">
        <div>
          <BrandLogo className="h-7 w-auto" />
          <p className="mt-3 text-sm text-muted-foreground">
            Business email, website hosting, and DNS management — set up for you. A division of Promonu.
          </p>
        </div>
        <FooterCol title="Services" links={[["Email Hosting", "/plans?type=email"], ["Website Hosting", "/plans?type=hosting"], ["Portfolio Enterprise", "/plans?type=portfolio"], ["View All Plans", "/plans"]]} />
        <FooterCol title="Company" links={[["About Us", "#about"], ["How It Works", "#how-it-works"], ["FAQ", "#faq"], ["Contact", "#consultation"]]} />
        <FooterCol title="Account" links={[["Client Login", "/login"], ["Get Started", "/plans"]]} />
      </div>
      <div className="border-t border-border">
        <div className="mx-auto flex max-w-[1180px] flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-muted-foreground sm:flex-row sm:px-6">
          <p>© {new Date().getFullYear()} HosterDash. A division of Promonu. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <a href="/privacy.html" className="transition hover:text-foreground">Privacy Policy</a>
            <a href="/terms.html" className="transition hover:text-foreground">Terms of Service</a>
            <a href="/subscribe.html" className="transition hover:text-foreground">Subscribe</a>
            <a href="/unsubscribe.html" className="transition hover:text-foreground">Unsubscribe</a>
            <span className="text-muted-foreground/70">Built by Ahsan Jamal</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div>
      <h4 className="font-display text-sm font-bold">{title}</h4>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
        {links.map(([label, href]) => (
          <li key={label}>
            {href.startsWith("#")
              ? <a href={href} className="transition hover:text-foreground">{label}</a>
              : <Link to={href} className="transition hover:text-foreground">{label}</Link>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Shared section wrapper ───────────────────────────────────
function Section({ id, eyebrow, title, sub, center, children }: {
  id?: string; eyebrow?: string; title?: string; sub?: string; center?: boolean; children: React.ReactNode;
}) {
  return (
    <section id={id} className="px-4 py-20 sm:px-6">
      <div className="mx-auto max-w-[1180px]">
        {(eyebrow || title) && (
          <div className={center ? "text-center" : ""}>
            {eyebrow && <span className="brand-pill-recommended">{eyebrow}</span>}
            {title && <h2 className="mt-4 font-display text-3xl font-bold tracking-tight">{title}</h2>}
            {sub && <p className={`mt-3 text-muted-foreground ${center ? "mx-auto max-w-2xl" : "max-w-2xl"}`}>{sub}</p>}
          </div>
        )}
        <div className={eyebrow || title ? "mt-10" : ""}>{children}</div>
      </div>
    </section>
  );
}
